import React from 'react';
import { useTranslation } from 'react-i18next';
import { CheckCircle, Clock, XCircle, AlertCircle, RotateCcw, RefreshCw } from 'lucide-react';
import type { IndexingTaskStatus } from '@/services/indexingService';
import { Button } from '@/components/Common/button';

interface IndexingTasksTableProps {
  tasks: IndexingTaskStatus[];
  indexingCount: number;
  indexedCount: number;
  failedCount: number;
  onRetryFailed?: () => void;
  isLoading?: boolean;
}

export const IndexingTasksTable: React.FC<IndexingTasksTableProps> = ({
  tasks,
  indexingCount,
  indexedCount,
  failedCount,
  onRetryFailed,
  isLoading = false,
}) => {
  const { t, i18n } = useTranslation();
  const dateLocale = i18n.language?.startsWith('fr') ? 'fr-FR' : 'en-US';

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'indexed':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'indexing':
        return <Clock className="h-4 w-4 text-blue-500 animate-spin" />;
      case 'failed':
        return <XCircle className="h-4 w-4 text-red-500" />;
      case 'not_indexed':
        return <AlertCircle className="h-4 w-4 text-gray-400" />;
      default:
        return null;
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'indexed':
        return t('knowledgeBasePage.indexingTasks.status.indexed');
      case 'indexing':
        return t('knowledgeBasePage.indexingTasks.status.indexing');
      case 'failed':
        return t('knowledgeBasePage.indexingTasks.status.failed');
      case 'not_indexed':
        return t('knowledgeBasePage.indexingTasks.status.notIndexed');
      default:
        return status;
    }
  };

  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'indexed':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'indexing':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'failed':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'not_indexed':
        return 'bg-gray-100 text-gray-600 border-gray-200';
      default:
        return 'bg-gray-100 text-gray-600 border-gray-200';
    }
  };

  if (tasks.length === 0) {
    return null;
  }

  return (
    <div className="bg-card border rounded-lg">
      <div className="p-4 border-b">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <h3 className="text-lg font-semibold">{t('knowledgeBasePage.indexingTasks.title')}</h3>
            {isLoading && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <RefreshCw className="h-3.5 w-3.5 animate-spin" />
                <span>{t('knowledgeBasePage.indexingTasks.reloading')}</span>
              </div>
            )}
          </div>
          <div className="flex items-center gap-4">
            <div className="flex gap-4 text-sm">
              <div className="flex items-center gap-1.5">
                <Clock className="h-4 w-4 text-blue-500" />
                <span className="text-xs text-muted-foreground">{t('knowledgeBasePage.indexingTasks.inProgress')}</span>
                <span className="text-blue-600 font-medium">{indexingCount}</span>
              </div>
              <div className="flex items-center gap-1.5">
                <CheckCircle className="h-4 w-4 text-green-500" />
                <span className="text-xs text-muted-foreground">{t('knowledgeBasePage.indexingTasks.indexedCount')}</span>
                <span className="text-green-600 font-medium">{indexedCount}</span>
              </div>
              <div className="flex items-center gap-1.5">
                <XCircle className="h-4 w-4 text-red-500" />
                <span className="text-xs text-muted-foreground">{t('knowledgeBasePage.indexingTasks.failedCount')}</span>
                <span className="text-red-600 font-medium">{failedCount}</span>
              </div>
            </div>
            {failedCount > 0 && onRetryFailed && (
              <Button
                size="sm"
                variant="outline"
                onClick={onRetryFailed}
                className="flex items-center gap-2"
              >
                <RotateCcw className="h-4 w-4" />
                {t('knowledgeBasePage.indexingTasks.retryFailed')}
              </Button>
            )}
          </div>
        </div>
      </div>

      <div className="overflow-x-auto max-h-[500px] overflow-y-auto">
        <table className="w-full text-sm">
          <thead className="bg-muted/50 border-b sticky top-0 z-10">
            <tr>
              <th className="text-left p-3 font-medium bg-muted/50">{t('knowledgeBasePage.indexingTasks.columns.status')}</th>
              <th className="text-left p-3 font-medium bg-muted/50">{t('knowledgeBasePage.indexingTasks.columns.document')}</th>
              <th className="text-center p-3 font-medium bg-muted/50">{t('knowledgeBasePage.indexingTasks.columns.chunks')}</th>
              <th className="text-left p-3 font-medium bg-muted/50">{t('knowledgeBasePage.indexingTasks.columns.updatedAt')}</th>
              <th className="text-left p-3 font-medium bg-muted/50">{t('knowledgeBasePage.indexingTasks.columns.error')}</th>
            </tr>
          </thead>
          <tbody className="divide-y">{tasks.map((task) => (
              <tr key={task.document_id} className="hover:bg-muted/30 transition-colors">
                <td className="p-3">
                  <div className={`inline-flex items-center gap-2 px-2 py-1 rounded-md border text-xs font-medium ${getStatusBadgeClass(task.status)}`}>
                    {getStatusIcon(task.status)}
                    {getStatusText(task.status)}
                  </div>
                </td>
                <td className="p-3">
                  <span className="text-sm font-medium truncate max-w-xs block" title={task.document_name}>
                    {task.document_name}
                  </span>
                </td>
                <td className="p-3 text-center">
                  <span className="text-xs font-mono text-muted-foreground">
                    {task.chunks_count > 0 ? task.chunks_count : '-'}
                  </span>
                </td>
                <td className="p-3">
                  <span className="text-xs text-muted-foreground">
                    {task.updated_at ? new Date(task.updated_at).toLocaleString(dateLocale, {
                      day: '2-digit',
                      month: '2-digit',
                      hour: '2-digit',
                      minute: '2-digit',
                    }) : '-'}
                  </span>
                </td>
                <td className="p-3">
                  {task.error_message ? (
                    <span
                      className="text-xs text-red-600 truncate max-w-xs block cursor-help"
                      title={task.error_message}
                    >
                      {task.error_message}
                    </span>
                  ) : (
                    <span className="text-xs text-muted-foreground">-</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
